import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { IgenomicsState } from '@ispm/core/interfaces/genomics-state.interface';
import { IReferenceItem } from '@ispm/core/interfaces/reference.interface';
import { IWorkflow } from '@ispm/core/interfaces/workflow.interface';
import { IWorkflowJobItem } from '@ispm/workflow/interfaces/job-item.interface';
import { get, isEqual } from 'lodash';
import { Action, select, Store } from '@ngrx/store';
import { IRevision } from '@ispm/test/interfaces/diagnostic-order.interface';
import { EMRIntegration, targetStepForPolling } from '@ispm-client/modules/pathways';
import { TestBarMoveToNextJobAction, TestBarPerformTasksBeforeMovingToNextJobAction } from '@ispm/shared/store/actions/test-bar.actions';
import { WorkflowConfigService } from '@ispm/workflow/services/workflow-config.service';
import { Observable } from 'rxjs/Observable';
import { getTestButtonConfirmButtonEnabledState, getTestButtonConfirmTooltipState } from '@ispm/test/store/test-bar.selectors';
import { IWorkflowRequest } from '@ispm/workflow/interfaces/workflow-request.interface';
import { TestBarEnableConfirmButtonAction } from '@ispm/test/store/test-bar.actions';
import { PathwayConfirmStepService } from '../services/pathway-confirm-step.service';
import { ActiveStepButtonDetails } from './models/active-step-button-details.model';
import { map, takeUntil } from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';
import { EmrIntegrationService } from '@ispm/test/services/emr-integration.service';
import {UpdateUserSelectionStatusAction} from "@ispm-client/modules/process-executor";
import {USER_SELECTION_STATUS} from "@ispm-client/modules/process-executor/store";
import {ProgressionService} from "@ispm/shared/services/progression.service";
import { DataNotificationService } from '@ispm-client/modules/selection-item/services/data-notification.service';
import { Subject } from 'rxjs';
import {TestService} from "@ispm/test/services/test.service";


@Component({
    selector: 'gen-step-confirmation-button',
    templateUrl: './step-confirmation-button.component.html',
    styleUrls: ['./step-confirmation-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StepConfirmationButtonComponent implements OnInit {
    private confirmButtonName = 'confirmButton';
    private hideAction: boolean;
    private stepExecutionType: string;
    protected destroyed$: Subject<void> = new Subject();
    confirmButtonValue: string;
    setDisableGenerateReport: boolean;
    confirmStepActionDetails$: Observable<ActiveStepButtonDetails>;

    @Input() currentWorkflow: IWorkflow;
    @Input() disableIfNotActive: boolean;
    @Input() currentRevision: IRevision;
    @Input() type: string;
    @Input() isDisabledGenerateReport: boolean = false;

    constructor(private store: Store<IgenomicsState>,
                private pathwayConfirmStepService: PathwayConfirmStepService,
                private clipboard: Clipboard,
                private _dataNotificationService: DataNotificationService,
                private emrIntegrationService: EmrIntegrationService,
                private testService: TestService,
                private progressionService: ProgressionService) {
    }

    ngOnInit(): void {
        this._dataNotificationService.changeEmitted$.pipe( takeUntil(this.destroyed$)).subscribe(res=> {
          if(res) {
            this.stepConfirmActiveStep();
          }
        })
      this.store.select('tests').take(1).subscribe(data => {
        if(get(data.diagnosticOrders.order, "derivedStatus.display") == "Defer") {
          this.store.dispatch(new TestBarEnableConfirmButtonAction(true));
        }
        this.confirmStepActionDetails$ = this.getActiveStepButtonDetails(this.currentWorkflow, this.currentRevision);
      });
    }

  confirmActiveStep() {
    const decisionAutoSave = get(this.testService, 'testConfiguration.decisionAutoSave', false);
    if(decisionAutoSave) {
      this._dataNotificationService.saveCarePlan();
    } else {
      this.stepConfirmActiveStep();
    }
  }

  stepConfirmActiveStep() {

      this.store.dispatch(new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.InProgress}));
      this.store.dispatch(new TestBarEnableConfirmButtonAction(false));
      const activeJob = this.pathwayConfirmStepService.getStepActiveJobItem(this.currentWorkflow, this.currentRevision);

        if (activeJob?.inputs == null) {
            this.store.dispatch(new UpdateUserSelectionStatusAction({status: USER_SELECTION_STATUS.NotInProgress}));
            return;
        }

        const input = this.getConfirmButtonInput(activeJob);
        const targetStep = this.getTargetStepForPolling(activeJob);
        const flowConfigInput = this.getFlowConfigurationInput(activeJob);
        const emrIntegration = this.getEMRIntegration(activeJob);
        let action: Action;
        const payload: any = {
            jobPurpose: activeJob.purpose
        };

        if (targetStep) {
            payload.targetStepForPolling = targetStep.value;
        }

        if (flowConfigInput) {
            payload.flowConfig = flowConfigInput.args;
        }
        if (emrIntegration) {
            payload.emrIntegration = emrIntegration;
        }

        const executionType = this.defineExecutionType(input, this.stepExecutionType);

        if (executionType === 'async') {
            action = new TestBarPerformTasksBeforeMovingToNextJobAction(payload);
        } else {
            payload.params = null;
            action = new TestBarMoveToNextJobAction(payload);
        }

        this.store.dispatch(action);

        if (action['payload'].jobPurpose === 'UIDocumentsPreview') {
          this.progressionService.search('PATHWAY_TEST')
            .take(1)
            .subscribe(progs => {
              if (progs && progs.length && progs.length > 0) {
                this.progressionService.update(progs[0]);
              }
            });
        }
    }

    private getActiveStepButtonDetails(workflow: IWorkflow, revision: IRevision): Observable<ActiveStepButtonDetails> {
        return this.setUpWorkflowStepActionOverride(workflow, revision).pipe(map(() =>
             this.generateActiveStepButtonDetails(workflow, revision)
        ));
    }

    private generateActiveStepButtonDetails(workflow: IWorkflowRequest, revision: IRevision): ActiveStepButtonDetails {
        const activeStepButtonDetails: ActiveStepButtonDetails = {
            isEnabled$: this.store.pipe(select(getTestButtonConfirmButtonEnabledState)),
            canUserPerformStep: this.pathwayConfirmStepService.checkIfUserCanPerformStep(workflow),
            type: this.type ?? 'proceed',
            isActive: false,
            isTooltipEnabled$: this.store.pipe(select(getTestButtonConfirmTooltipState))
        };

        const activeJob = this.pathwayConfirmStepService.getStepActiveJobItem(workflow, revision);
        if (activeJob && activeJob.inputs) {
            const input = this.getConfirmButtonInput(activeJob);
            if (input && input.value) {
                activeStepButtonDetails.text = this.confirmButtonValue ?? input.value;
                activeStepButtonDetails.disabledTooltipText = (input as any).tooltipOnDisabled;
                if (input.enabledByDefault) {
                    this.store.dispatch(new TestBarEnableConfirmButtonAction(true));
                }

                // if the step has confirm button, the active state will depend on hide action value
                activeStepButtonDetails.isActive = !this.hideAction;
            }
        }

        return activeStepButtonDetails;
    }

    private defineExecutionType(input, stepExecutiontypeFromOverride): string {
        if (!!stepExecutiontypeFromOverride) {
            return stepExecutiontypeFromOverride;
        }

        if (input?.args === 'async') {
            return 'async';
        }

        return 'sync';
    }

    private getConfirmButtonInput(job: IWorkflowJobItem): IReferenceItem {
        return job.inputs.find(val => val.type === this.confirmButtonName);
    }

    private getTargetStepForPolling(job: IWorkflowJobItem): IReferenceItem {
        return job.inputs.find(val => val.key === targetStepForPolling);
    }

    private getEMRIntegration(job: IWorkflowJobItem): IReferenceItem {
      const copyToClipboardType = 'copyToClipboard';
      const emrIntegration = job.inputs.find(val => val.key === EMRIntegration);
      if (!!emrIntegration && emrIntegration.type === copyToClipboardType) {
          const valueToCopy = this.emrIntegrationService.redirectToEMRIntegration(emrIntegration);
          this.clipboard.copy(valueToCopy);
          return undefined;
      }
      return emrIntegration;
    }

    private getFlowConfigurationInput(job: IWorkflowJobItem): IReferenceItem {
        return job.inputs.find(val => val.key === WorkflowConfigService.PROCESS_CONFIG_INPUT_TYPE);
    }

    private setUpWorkflowStepActionOverride(currentWorkflow, currentRevision): Observable<any> {
        const workflowActionOverride$ = this.pathwayConfirmStepService.getWorkflowStepActionOverride();
        return workflowActionOverride$.pipe(map(workflowAction => {
            this.hideAction = get(workflowAction, 'hideActionButton', false);

            if (!this.hideAction) {
                this.updateConfirmButtonBehaviour(get(workflowAction, 'actionButton', undefined), currentWorkflow, currentRevision);
            }
        }));
    }

    private updateConfirmButtonBehaviour(confirmButtonDescriptor: any, currentWorkflow, currentRevision): void {
        this.confirmButtonValue = undefined;
        this.stepExecutionType = undefined;
        if (confirmButtonDescriptor && confirmButtonDescriptor.type === this.confirmButtonName && currentRevision) {

            this.confirmButtonValue = get(confirmButtonDescriptor, 'value', undefined);
            this.stepExecutionType = get(confirmButtonDescriptor, 'args', undefined);

            currentRevision.items.forEach(val => {
                if (val.id === currentWorkflow.activeJob) {
                    val.inputs.forEach(input => {
                        if (input.type === this.confirmButtonName) {
                            Object.assign(input, confirmButtonDescriptor);
                            if (input.enabledByDefault) {
                                this.store.dispatch(new TestBarEnableConfirmButtonAction(true));
                            }
                        }
                    });
                }
            });
        }
    }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
